import {
  Backdrop,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Fade,
  Modal,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import WO2Button from 'src/common/components/button/Button';
import { useStyles } from 'src/themes';
import { ReportTemplate, TemplateType } from '../store/types';

export interface Props {
  row: ReportTemplate | null;
  handleImport: (name: string, file?: File) => void;
  handleClose: () => void;
  isOpen: boolean;
  templateType: TemplateType;
  setTemplateType: (type: TemplateType) => void;
}

export const ReportTemplateModal = (props: Props): JSX.Element => {
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const [importErrors, setImportErrors] = useState<string>();
  const [name, setName] = useState<string>('');
  const classes = useStyles();

  const isCreate = !props.row;
  const isWord = props.templateType === 'Word';
  const allowedExtensions = isWord ? ['.docx', '.zip'] : ['.xlsx', '.zip'];
  const acceptAttr = allowedExtensions.join(',');

  const file = acceptedFiles.map((selectedFile) => (
    <span key={selectedFile.name}>
      {selectedFile.name} - {selectedFile.size} bytes
    </span>
  ));

  const handleValidFile = (files: File[]) => {
    const selectedFile = files[0];
    const ext = selectedFile.name
      .substring(selectedFile.name.lastIndexOf('.'))
      .toLowerCase();

    if (selectedFile.size / 1000000 > 50) {
      setImportErrors('Attachment has exceeded size limit (50MB)');
    } else if (!allowedExtensions.includes(ext)) {
      setImportErrors(
        `Only ${allowedExtensions.join(', ')} files can be uploaded for ${props.templateType} templates.`
      );
    } else {
      setImportErrors('');
    }

    setAcceptedFiles(files);
  };

  useEffect(() => {
    setAcceptedFiles([]);
    setImportErrors('');
  }, [props.isOpen]);

  useEffect(() => {
    setName(props.row?.name ?? '');
  }, [props.row, props.isOpen]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={props.isOpen}
      onClose={() => {
        props.handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={props.isOpen}>
        <Card
          elevation={0}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            padding: 1,
          }}
        >
          <CardHeader
            title={
              <Typography variant="h3">
                {props.row ? 'Update' : 'Upload'} {props.templateType} Template
              </Typography>
            }
          />
          <CardContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              {isCreate && (
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={props.templateType}
                  onChange={(_, val: TemplateType | null) => {
                    if (val) {
                      props.setTemplateType(val);
                    }
                  }}
                >
                  <ToggleButton value="Word">Word template</ToggleButton>
                  <ToggleButton value="Excel">Excel template</ToggleButton>
                </ToggleButtonGroup>
              )}

              <Dropzone onDrop={(files) => handleValidFile(files)} multiple={false}>
                {({ getRootProps, getInputProps }) => (
                  <Box
                    component="div"
                    data-testid="dropzone"
                    {...getRootProps({ className: classes.dropzone })}
                    margin={0}
                  >
                    <input
                      {...getInputProps({
                        accept: acceptAttr,
                        type: 'file',
                      })}
                    />
                    <p>
                      Drag and drop your file here, or click to select file
                      <br />
                      <small>
                        Accepted: {allowedExtensions.join(', ')}
                      </small>
                    </p>
                  </Box>
                )}
              </Dropzone>

              {acceptedFiles.length > 0 && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography>File: {file}</Typography>
                  {importErrors && importErrors.length > 0 && (
                    <Typography color="error">{importErrors}</Typography>
                  )}
                </Box>
              )}
            </Box>
          </CardContent>
          <CardActions>
            <Box sx={{ flex: 1 }} />
            <Box display="flex" gap={2}>
              <WO2Button
                variant="outlined"
                onClick={() => {
                  props.handleClose();
                  setImportErrors('');
                  setAcceptedFiles([]);
                }}
              >
                Cancel
              </WO2Button>
              <WO2Button
                variant="contained"
                color="primary"
                disabled={acceptedFiles.length < 1 || name.length === 0 || !!importErrors}
                onClick={() => {
                  if (acceptedFiles && acceptedFiles.length === 1) {
                    const file = acceptedFiles.length === 1 ? acceptedFiles[0] : undefined;
                    props.handleImport(name, file);
                    acceptedFiles.length = 0;
                    acceptedFiles.splice(0, acceptedFiles.length);
                  }
                }}
              >
                {props.row ? 'Update' : 'Upload'}
              </WO2Button>
            </Box>
          </CardActions>
        </Card>
      </Fade>
    </Modal>
  );
};
